import { useState, useEffect, useCallback, useRef, useMemo, memo } from "react";
import { createPortal } from "react-dom";
import { X, Percent, GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatPYGUnit } from "../../utils/format";
import { toast } from "sonner";
import { UNIDADES, DEFAULT_PCT, opts, fmt, seleccionarAlEntrar } from "./constants";   // Fix IP: celdas de número como en una planilla
import { PriceCellInput } from "./PriceCellInput";
import { computeItemLive } from "./budgetCalcs";

// PDF REV2 #4 — Detector de "materiales menores" para auto-activar modo % del grupo.
const isMenoresDescription = (desc) => {
  if (!desc) return false;
  const s = String(desc).toLowerCase().trim();
  return s.includes("materiales menores") || s.includes("material menor") || s.includes("mat. menores") || s.includes("mat menores");
};

export const ItemRow = memo(function ItemRow({ item, idx, pct, groupPct, groupMatTotal, precioInputMode, displayNum, onChange, onRemove, onSearch, suggestions, showSugg, onSuggFocus, onSuggBlur, hidePrice, dragId, dragEnabled, selected, onToggleSelect }) {
  const is_group = item.is_group;
  const is_menores = item.tipo_calculo === "menores_pct";

  // BLOQUE 59 — celda de selección múltiple (checkbox), compartida por las 3 variantes de fila.
  const selectCell = (
    <td className="px-1 py-1 w-6 text-center">
      <input
        type="checkbox"
        checked={!!selected}
        onChange={(e) => onToggleSelect(idx, e.nativeEvent?.shiftKey)}
        className="w-3.5 h-3.5 accent-[#2E6DA4] cursor-pointer"
        data-testid={`item-select-${idx}`}
        aria-label="Seleccionar fila"
      />
    </td>
  );

  // PDF REV4 B.2 — Auto-grow del textarea de descripción.
  // Recalcula altura al montar (budgets con texto largo precargado) y en cada cambio.
  // No pisa el handle manual: el usuario sigue pudiendo achicar con resize-y; al editar de nuevo,
  // se reajusta al contenido. Setear height='auto' antes de leer scrollHeight evita "stickiness"
  // cuando el contenido baja de tamaño.
  const descRef = useRef(null);
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [item.descripcion]);

  // PDF REV3 #7 — Drag & drop por fila (excepto si se desactiva explícitamente)
  const sortable = useSortable({ id: dragId || `row-${idx}`, disabled: !dragEnabled });
  const dragStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.6 : 1,
    backgroundColor: sortable.isDragging ? "#FEF3C7" : undefined,
    boxShadow: selected ? "inset 0 0 0 2px #2E6DA4" : undefined,
    position: "relative",
    zIndex: sortable.isDragging ? 50 : "auto",
  };

  // BLOQUE 54 — ref de dnd-kit para poder arrastrar la fila. BLOQUE 61: ya NO se
  // combina con `rowVirtualizer.measureElement` — ver comentario en BudgetForm.jsx
  // sobre por qué la medición dinámica por ResizeObserver se eliminó (causaba un
  // ciclo de remedición/re-render que podía congelar el navegador con cómputos
  // grandes). El auto-grow del textarea de más abajo sigue funcionando igual
  // (es puramente visual), solo dejó de "avisarle" al virtualizador.
  const setRowRef = sortable.setNodeRef;

  // Pedido video 2 #3: usar % del grupo si están definidos, sino caer al global.
  const pick = (field, def) => {
    if (groupPct && groupPct[field] != null && groupPct[field] !== "") return Number(groupPct[field]);
    return Number(pct[field] != null ? pct[field] : def);
  };

  // ⚠️ NO duplicar cálculos: única fuente = budgetCalcs.js::computeItemLive (↔ backend
  // calc_item). BLOQUE 7 (Feb 2026) — bug reiterativo "Total M.O. fantasma": una copia
  // local acá cruzaba total_mat/total_mo (costo c/IVA) con los valores de VENTA. Si hace
  // falta corregir una fórmula, corregirla en computeItemLive Y calc_item, nunca acá.
  const calcVals = computeItemLive(item, pct, groupPct, precioInputMode, groupMatTotal);
  const calc = (field) => calcVals[field] ?? 0;

  if (is_group) {
    const nivel = Number(item.nivel_jerarquia || 0);
    if (nivel >= 1) {
      // FIX jerarquía — sub-grupo (ej. "4.1 PLANTA BAJA"): sangría según nivel,
      // fondo gris distinto, sin cantidad/precio ni % editables (como los grupos).
      const indentPx = 8 + (nivel - 1) * 16;
      return (
        <tr
          ref={setRowRef}
          data-index={idx}
          style={dragStyle}
          className="bg-gray-100 border-l-4 border-gray-300"
          data-testid="item-row-subgroup"
        >
          <td className="px-1 py-1 w-7 text-center">
            {dragEnabled && (
              <button
                type="button"
                {...sortable.attributes}
                {...sortable.listeners}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 hover:bg-white/40 rounded p-0.5"
                title="Arrastrar para reordenar"
                data-testid={`drag-handle-${idx}`}
                aria-label="Arrastrar fila"
              >
                <GripVertical size={13} />
              </button>
            )}
          </td>
          {selectCell}
          <td className="px-2 py-1 w-14">
            <input value={item.numero} onChange={e => onChange(idx, "numero", e.target.value)}
              className="w-full bg-transparent text-xs font-bold text-gray-500 border-0 focus:ring-1 focus:ring-gray-400 rounded px-1" placeholder={displayNum || "1.1"} />
          </td>
          <td className="px-2 py-1" colSpan={hidePrice ? 3 : 18}>
            <div className="flex items-center gap-2" style={{ paddingLeft: `${indentPx}px` }}>
              <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider border border-gray-300 rounded px-1 py-0.5 bg-white/70 flex-shrink-0">{nivel >= 2 ? "Sub-sub-sección" : "Sub-sección"}</span>
              <input value={item.descripcion} onChange={e => onChange(idx, "descripcion", e.target.value)}
                className="flex-1 bg-transparent text-xs font-bold text-gray-600 border-0 focus:ring-1 focus:ring-gray-400 rounded px-1 uppercase" placeholder="SUB-SECCIÓN (EJ: PLANTA BAJA)..." />
            </div>
          </td>
          <td className="px-2 py-1 text-center w-10">
            <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600"><X size={13} /></button>
          </td>
        </tr>
      );
    }
    // Pedido video 2 #3: cada grupo tiene SUS PROPIOS % editables (Ganancia Mat, GA, GMO)
    return (
      <tr
        ref={setRowRef}
        data-index={idx}
        style={dragStyle}
        className="bg-[#D9EAF8]"
        data-testid="item-row-group"
      >
        <td className="px-1 py-1.5 w-7 text-center">
          {dragEnabled && (
            <button
              type="button"
              {...sortable.attributes}
              {...sortable.listeners}
              className="cursor-grab active:cursor-grabbing text-[#1A4B7A]/60 hover:text-[#1A4B7A] hover:bg-white/40 rounded p-0.5"
              title="Arrastrar para reordenar"
              data-testid={`drag-handle-${idx}`}
              aria-label="Arrastrar fila"
            >
              <GripVertical size={13} />
            </button>
          )}
        </td>
        {selectCell}
        <td className="px-2 py-1.5 w-14">
          <input value={item.numero} onChange={e => onChange(idx, "numero", e.target.value)}
            className="w-full bg-transparent text-xs font-bold text-[#1A4B7A] border-0 focus:ring-1 focus:ring-[#2E6DA4] rounded px-1" placeholder={displayNum || "1.0"} />
        </td>
        <td className="px-2 py-1.5" colSpan={hidePrice ? 3 : 10}>
          <input value={item.descripcion} onChange={e => onChange(idx, "descripcion", e.target.value)}
            className="w-full bg-transparent text-sm font-bold text-[#1A4B7A] border-0 focus:ring-1 focus:ring-[#2E6DA4] rounded px-1 uppercase" placeholder="NOMBRE DE LA SECCIÓN..." />
        </td>
        {!hidePrice && (<>
        {/* PDF REV1 B.2: G.Mat ocupa 1 columna (Tot.G.M), G.A 1 col (Tot.G.A), G.M.O 1 col (Tot.G.MO).
            Las 5 columnas restantes (P.UNIT.MAT, P.UNIT.MO, P.UNITARIO, IVA, PRECIO TOTAL) van vacías. */}
        <td className="px-1 py-1 text-center">
          <div className="flex items-center justify-end gap-1">
            <span className="text-[10px] font-bold text-[#1A4B7A]">G.Mat:</span>
            <div className="flex items-center border border-[#1A4B7A] rounded bg-white">
              <input
                type="number" min="0" max="100" step="0.5"
                value={item.ganancia_mat_pct ?? ""}
                placeholder={String(pct.ganancia_mat_pct || 10)}
                onChange={e => onChange(idx, "ganancia_mat_pct", e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                className="w-10 text-[10px] font-bold text-[#1A4B7A] text-center border-0 focus:outline-none bg-transparent"
                {...seleccionarAlEntrar}
                data-testid={`group-pct-mat-${idx}`}
              />
              <span className="text-[10px] text-[#1A4B7A] font-bold pr-1">%</span>
            </div>
          </div>
        </td>
        <td className="px-1 py-1 text-center">
          <div className="flex items-center justify-end gap-1">
            <span className="text-[10px] font-bold text-[#1A4B7A]">G.A:</span>
            <div className="flex items-center border border-[#1A4B7A] rounded bg-white">
              <input
                type="number" min="0" max="100" step="0.5"
                value={item.ga_pct ?? ""}
                placeholder={String(pct.ga_pct || 10)}
                onChange={e => onChange(idx, "ga_pct", e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                className="w-10 text-[10px] font-bold text-[#1A4B7A] text-center border-0 focus:outline-none bg-transparent"
                {...seleccionarAlEntrar}
                data-testid={`group-pct-ga-${idx}`}
              />
              <span className="text-[10px] text-[#1A4B7A] font-bold pr-1">%</span>
            </div>
          </div>
        </td>
        <td className="px-1 py-1 text-center">
          <div className="flex items-center justify-end gap-1">
            <span className="text-[10px] font-bold text-[#1A4B7A]">G.M.O:</span>
            <div className="flex items-center border border-[#1A4B7A] rounded bg-white">
              <input
                type="number" min="0" max="100" step="0.5"
                value={item.gmo_pct ?? ""}
                placeholder={String(pct.gmo_pct || 45)}
                onChange={e => onChange(idx, "gmo_pct", e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                className="w-10 text-[10px] font-bold text-[#1A4B7A] text-center border-0 focus:outline-none bg-transparent"
                {...seleccionarAlEntrar}
                data-testid={`group-pct-gmo-${idx}`}
              />
              <span className="text-[10px] text-[#1A4B7A] font-bold pr-1">%</span>
            </div>
          </div>
        </td>
        <td colSpan={5}></td>
        </>)}
        <td className="px-2 py-1.5 text-center w-10">
          <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600"><X size={13} /></button>
        </td>
      </tr>
    );
  }

  return (
    <tr
      ref={setRowRef}
      data-index={idx}
      style={dragStyle}
      className="hover:bg-gray-50/50 border-b border-gray-50"
      data-testid={`item-row-${idx}`}
    >
      <td className="px-1 py-1 w-7 text-center">
        {dragEnabled && (
          <button
            type="button"
            {...sortable.attributes}
            {...sortable.listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#2E6DA4] hover:bg-gray-100 rounded p-0.5"
            title="Arrastrar para reordenar"
            data-testid={`drag-handle-${idx}`}
            aria-label="Arrastrar fila"
          >
            <GripVertical size={13} />
          </button>
        )}
      </td>
      {selectCell}
      <td className="px-1 py-1 w-14">
        <input value={displayNum || ""} readOnly tabIndex={-1}
          title="N° automático (se recalcula según el orden y la sección)"
          className="w-full border border-gray-100 bg-gray-50 rounded px-1 py-0.5 text-xs text-center text-gray-500 cursor-default focus:outline-none"
          data-testid={`item-numero-${idx}`} />
      </td>
      <td className="px-1 py-1 relative">
        <div className="flex items-center gap-1">
          <textarea ref={descRef} value={item.descripcion} onChange={e => {
            const newVal = e.target.value;
            onChange(idx, "descripcion", newVal);
            onSearch(newVal, idx);
            // PDF REV2 #4 — Auto-detectar "materiales menores" → activar modo % del grupo
            // Solo si la descripción matches Y aún no es menores_pct Y no tiene precio cargado.
            if (isMenoresDescription(newVal) && item.tipo_calculo !== "menores_pct" && !item.costo_unit_mat) {
              onChange(idx, "tipo_calculo", "menores_pct");
              onChange(idx, "menores_pct", 5);
              onChange(idx, "unidad", "glo");
              onChange(idx, "cantidad", 1);
            }
          }}
            onFocus={() => onSuggFocus(idx)} onBlur={() => onSuggBlur()}
            title={item.descripcion || ""}
            rows={1}
            className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-xs focus:ring-1 focus:ring-[#2E6DA4] focus:border-transparent resize-y whitespace-pre-wrap break-words leading-snug min-h-[24px]" placeholder="Descripción del ítem..."
            data-testid={`item-desc-${idx}`} />
          {/* PDF REV2 #4 — Badge contextual para convertir manualmente a "% del grupo" */}
          {isMenoresDescription(item.descripcion) && item.tipo_calculo !== "menores_pct" && (
            <button
              type="button"
              onClick={() => {
                onChange(idx, "tipo_calculo", "menores_pct");
                onChange(idx, "menores_pct", 5);
                onChange(idx, "unidad", "glo");
                onChange(idx, "cantidad", 1);
                onChange(idx, "costo_unit_mat", 0);
              }}
              className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold border border-amber-300 transition-colors"
              title="Convertir a 'Materiales menores (% del grupo)' — calcula automáticamente como % del subtotal de materiales del grupo"
              data-testid={`convert-to-menores-${idx}`}
            >
              <Percent size={10} /> %
            </button>
          )}
          {item.tipo_calculo === "menores_pct" && (
            <button
              type="button"
              onClick={() => {
                onChange(idx, "tipo_calculo", "fijo");
                onChange(idx, "menores_pct", null);
              }}
              className="flex-shrink-0 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 text-[10px] font-bold border border-gray-200 transition-colors"
              title="Volver a precio fijo (desactivar % del grupo)"
              data-testid={`disable-menores-${idx}`}
            >
              ×%
            </button>
          )}
        </div>
        {showSugg && suggestions.length > 0 && (
          <div className="absolute top-full left-0 z-50 w-80 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
            {suggestions.map((s, si) => (
              <button key={si} onMouseDown={() => {
                const priceForMode = precioInputMode === "con_iva"
                  ? Number(s.precio_iva_inc || 0)
                  : Number(s.precio_iva_inc || 0) / 1.10;
                onChange(idx, "descripcion", s.descripcion);
                onChange(idx, "unidad", s.unidad || "un");
                onChange(idx, "costo_unit_mat", priceForMode);
                onSuggBlur();
              }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#EBF3FB] flex items-center justify-between gap-2 border-b border-gray-50">
                <div className="flex-1 min-w-0">
                  {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                  <p className="whitespace-normal break-words font-medium text-gray-800" title={s.descripcion}>{s.descripcion}</p>
                  <p className="text-[#2E6DA4] text-[0.6rem]">{s.proveedor}</p>
                </div>
                <span className="text-[#2E6DA4] font-bold flex-shrink-0">{fmt(s.precio_iva_inc)}</span>
              </button>
            ))}
          </div>
        )}
      </td>
      <td className="px-1 py-1 w-16">
        <select value={item.unidad} onChange={e => onChange(idx, "unidad", e.target.value)}
          className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs bg-white focus:ring-1 focus:ring-[#2E6DA4]"
          data-testid={`item-unidad-${idx}`}>
          {UNIDADES.map(u => <option key={u}>{u}</option>)}
        </select>
      </td>
      <td className="px-1 py-1 w-16">
        <input type="number" min="0" value={item.cantidad} onChange={e => onChange(idx, "cantidad", parseFloat(e.target.value) || 0)}
          className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs text-right focus:ring-1 focus:ring-[#2E6DA4]"
          {...seleccionarAlEntrar}
          data-testid={`item-cantidad-${idx}`} />
      </td>
      {!hidePrice && (<>
      <td className="px-1 py-1 w-24">
        {is_menores ? (
          <div className="flex items-center gap-1 border border-amber-300 bg-amber-50 rounded px-1.5 py-0.5">
            <input
              type="number" min="0" max="100" step="0.5"
              value={item.menores_pct ?? ""}
              placeholder="5"
              onChange={e => onChange(idx, "menores_pct", e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
              className="w-10 text-[11px] font-bold text-amber-700 text-center border-0 bg-transparent focus:outline-none"
              {...seleccionarAlEntrar}
              data-testid={`item-menores-pct-${idx}`}
              title="% sobre el subtotal del grupo (materiales sin IVA)"
            />
            <span className="text-[10px] text-amber-700 font-bold">%</span>
          </div>
        ) : (
          <PriceCellInput
            value={item.costo_unit_mat}
            onChange={(v) => onChange(idx, "costo_unit_mat", v)}
            type="mat"
            testId={`item-cu-mat-${idx}`}
            placeholder="0"
            combinatorMecanismos={item.combinator_mecanismos}
            combinatorCables={item.combinator_cables}
            onCombinatorChange={(field, rows) => onChange(idx, field, rows)}
            bocaMetraje={item.boca_metraje}
            onBocaSelect={(precio, ref, metraje) => {
              // Fix AJ (2026-09-04): el ítem queda vinculado a la boca y con el
              // metraje de esta obra — es lo que hace que el consumo mida bien.
              onChange(idx, "catalogo_ref", ref);
              onChange(idx, "boca_metraje", metraje);
            }}
          />
        )}
      </td>
      {[
        [calc("mat_sin_iva"), "text-gray-600"],
        [calc("total_mat"), "text-blue-600"],
        [calc("costo_unit_mo") || "", ""],
        [calc("mo_sin_iva"), "text-gray-600"],
        [calc("total_mo"), "text-blue-600"],
        [calc("costo_total"), "text-gray-700 font-semibold"],
        [calc("tot_gm"), "text-orange-600"],
        [calc("tot_ga"), "text-orange-600"],
        [calc("tot_gmo"), "text-orange-600"],
        [calc("pu_mat"), "text-[#2E6DA4] font-semibold", true],
        [calc("pu_mo"), "text-[#2E6DA4] font-semibold", true],
        [calc("precio_unit"), "text-[#1A4B7A] font-bold", true],
        [calc("iva_item"), "text-orange-500"],
        [calc("total_final"), "text-[#1A4B7A] font-bold"],
      ].map(([val, cls, isUnit], ci) => (
        <td key={ci} className={`px-2 py-1 text-xs text-right whitespace-nowrap ${cls}`}>
          {ci === 2 ? (
            <PriceCellInput
              value={item.costo_unit_mo}
              onChange={(v) => onChange(idx, "costo_unit_mo", v)}
              type="mo"
              testId={`item-cu-mo-${idx}`}
              placeholder="0"
            />
          ) : (
            typeof val === "number"
              ? (isUnit ? formatPYGUnit(val) : fmt(Math.round(val)))
              : val
          )}
        </td>
      ))}
      </>)}
      <td className="px-1 py-1 w-10 text-center">
        <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600 transition-colors">
          <X size={13} />
        </button>
      </td>
    </tr>
  );
});

// ─── Budget Form ──────────────────────────────────────────────────────────────
